import type { AllPerpMetasResponse, PerpDexsResponse } from '@nktkas/hyperliquid/api/info';
import { type QueryClient, queryOptions, useQuery } from '@tanstack/react-query';

import { hyperliquidInfo } from '@/lib/hyperliquid/client';
import { USDC_TOKEN_INDEX } from '@/lib/hyperliquid/constants';
import { perpAssetIndex } from '@/lib/hyperliquid/orders';
import type { PerpMarket } from '@/lib/hyperliquid/types';

export type PerpDex = {
  /** Position in `perpDexs`, which is the `dexIndex` used in asset ids. */
  index: number;
  /** Short name used in coin prefixes and `dex` request params; `''` for the validator-operated dex. */
  name: string;
  deployer?: string;
  meta: AllPerpMetasResponse[number];
};

export type PerpsMetadata = {
  dexs: PerpDex[];
  marketByCoin: Record<string, PerpMarket>;
  marketByAsset: Record<number, PerpMarket>;
};

export const perpsDexsQueryKey = () => ['perps', 'perpDexs'] as const;

export function buildPerpsMetadata(
  dexs: PerpDexsResponse,
  metas: AllPerpMetasResponse,
): PerpsMetadata {
  const result: PerpsMetadata = { dexs: [], marketByCoin: {}, marketByAsset: {} };
  dexs.forEach((dex, index) => {
    const meta = metas[index];
    if (!meta) throw new Error(`Missing metadata for perps dex ${dex?.name ?? ''}`);
    if (meta.collateralToken !== USDC_TOKEN_INDEX) return;
    result.dexs.push({ index, name: dex?.name ?? '', deployer: dex?.fullName, meta });
    meta.universe.forEach((market, universeIndex) => {
      const perpMarket = {
        ...market,
        assetIndex: perpAssetIndex(index, universeIndex),
        deployer: dex?.fullName,
      };
      result.marketByCoin[market.name] = perpMarket;
      result.marketByAsset[perpMarket.assetIndex] = perpMarket;
    });
  });
  return result;
}

export const perpsMetadataQueryOptions = () =>
  queryOptions({
    queryKey: perpsDexsQueryKey(),
    queryFn: async ({ signal }) => {
      const [dexs, metas] = await Promise.all([
        hyperliquidInfo.perpDexs(signal),
        hyperliquidInfo.allPerpMetas(signal),
      ]);
      return buildPerpsMetadata(dexs, metas);
    },
    // Only mounted on the feature-gated perps page; asset ids stay fixed for its lifetime.
    enabled: typeof window !== 'undefined',
    staleTime: Infinity,
  });

const selectDexs = (data: PerpsMetadata) => data.dexs;

/** Every USDC-collateralized dex, preserving the exchange's original asset indices. */
export function usePerpDexs() {
  return useQuery({ ...perpsMetadataQueryOptions(), select: selectDexs }).data;
}

const EMPTY_METADATA: PerpsMetadata = { dexs: [], marketByCoin: {}, marketByAsset: {} };

/** Cached metadata for imperative reads (mutations, stream callbacks); empty until fetched. */
export function getPerpsMetadata(queryClient: QueryClient) {
  return queryClient.getQueryData<PerpsMetadata>(perpsDexsQueryKey()) ?? EMPTY_METADATA;
}

export function requirePerpsMarket(marketByCoin: Record<string, PerpMarket>, coin: string) {
  const market = Object.hasOwn(marketByCoin, coin) ? marketByCoin[coin] : undefined;
  if (!market) throw new Error(`No market data for ${coin}`);
  return market;
}
